/**
 * Modal de Envio de Mensagem/Email
 * 
 * Permite enviar mensagem ou email para outros usuários do sistema
 * 
 * @author Equipe GestaoeCRM
 * @date 06/11/2025
 */

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Send, User, Mail, MessageSquare, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '@/services/api';
import { z } from 'zod';

interface SendMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

// Schema de validação
const messageSchema = z.object({
  recipientId: z.string().min(1, 'Selecione um destinatário'),
  subject: z.string().min(3, 'Assunto deve ter pelo menos 3 caracteres').max(200, 'Assunto deve ter no máximo 200 caracteres'),
  message: z.string().min(10, 'Mensagem deve ter pelo menos 10 caracteres').max(5000, 'Mensagem deve ter no máximo 5000 caracteres'),
  sendEmail: z.boolean().default(false),
});

type MessageFormData = z.infer<typeof messageSchema>;

/**
 * Componente SendMessageModal
 * 
 * Modal para enviar mensagem ou email para outros usuários
 */
const SendMessageModal: React.FC<SendMessageModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<MessageFormData>({
    recipientId: '',
    subject: '',
    message: '',
    sendEmail: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Buscar usuários
  const { data: usersData, isLoading: loadingUsers } = useQuery({
    queryKey: ['users', 'message', searchTerm],
    queryFn: async () => {
      const response = await api.get('/users', {
        params: {
          page: 1,
          limit: 50,
          search: searchTerm,
          active: true,
        },
      });
      return response.data.data as User[];
    },
    enabled: isOpen,
    staleTime: 30000,
  });

  const users = usersData || [];

  // Resetar formulário ao abrir/fechar
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        recipientId: '',
        subject: '',
        message: '',
        sendEmail: false,
      });
      setErrors({});
      setSearchTerm('');
    }
  }, [isOpen]);

  // Validar formulário
  const validateForm = (): boolean => {
    try {
      messageSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  // Handler de submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Implementar endpoint de envio de mensagem/email no backend
      // Por enquanto, apenas simular o envio
      await new Promise((resolve) => setTimeout(resolve, 1000));

      toast.success(
        formData.sendEmail
          ? 'Email enviado com sucesso!'
          : 'Mensagem enviada com sucesso!'
      );

      onSuccess?.();
      onClose();
    } catch (error: any) {
      // console.error('Erro ao enviar mensagem:', error);
      toast.error(
        error?.response?.data?.message ||
        'Erro ao enviar mensagem. Tente novamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Se não estiver aberto, não renderizar
  if (!isOpen) {
    return null;
  }

  // Buscar usuário selecionado
  const selectedUser = users.find((u) => u.id === formData.recipientId);

  return (
    <div 
      className="bg-background-glass backdrop-blur-glass border border-border-glass rounded-button shadow-glass max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border-glass/30">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-500/20 rounded-button border border-blue-500/30">
            <MessageSquare className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-text-primary">
              Enviar Mensagem/Email
            </h2>
            <p className="text-sm text-text-muted mt-1">
              Envie uma mensagem ou email para outro usuário
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-background-glass-hover rounded-button transition-colors"
          aria-label="Fechar"
        >
          <X className="w-5 h-5 text-text-muted hover:text-text-primary" />
        </button>
      </div>

      {/* Conteúdo */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Seleção de Destinatário */}
        <div>
          <label className="block text-sm font-semibold text-text-primary mb-2">
            <User className="w-4 h-4 inline mr-1" />
            Destinatário
          </label>
          <div className="space-y-2">
            {/* Busca de usuários */}
            <input
              type="text"
              placeholder="Buscar usuário por nome ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-border-glass rounded-button bg-background-glass text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
            />

            {/* Lista de usuários */}
            {loadingUsers ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-secondary" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-4 text-text-muted text-sm">
                Nenhum usuário encontrado
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto border border-border-glass rounded-button bg-background-glass-hover">
                {users.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, recipientId: user.id }));
                      setSearchTerm(user.name);
                    }}
                    className={`w-full text-left px-4 py-3 hover:bg-background-glass transition-colors border-b border-border-glass/50 last:border-b-0 ${
                      formData.recipientId === user.id
                        ? 'bg-secondary/20 border-l-4 border-secondary'
                        : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-text-primary">{user.name}</div>
                        <div className="text-sm text-text-muted">{user.email}</div>
                      </div>
                      <span className="text-xs px-2 py-1 bg-primary/20 text-primary rounded-full">
                        {user.role}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {errors.recipientId && (
            <p className="mt-1 text-sm text-red-400 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.recipientId}
            </p>
          )}
        </div>

        {/* Assunto */}
        <div>
          <label className="block text-sm font-semibold text-text-primary mb-2">
            Assunto
          </label>
          <input
            type="text"
            value={formData.subject}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, subject: e.target.value }))
            }
            placeholder="Digite o assunto da mensagem..."
            className="w-full px-4 py-2 border border-border-glass rounded-button bg-background-glass text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
          />
          {errors.subject && (
            <p className="mt-1 text-sm text-red-400 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.subject}
            </p>
          )}
        </div>

        {/* Mensagem */}
        <div>
          <label className="block text-sm font-semibold text-text-primary mb-2">
            Mensagem
          </label>
          <textarea
            value={formData.message}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, message: e.target.value }))
            }
            placeholder="Digite sua mensagem..."
            rows={8}
            className="w-full px-4 py-2 border border-border-glass rounded-button bg-background-glass text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent resize-none"
          />
          <div className="flex items-center justify-between mt-1">
            {errors.message && (
              <p className="text-sm text-red-400 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.message}
              </p>
            )}
            <p className="text-xs text-text-muted ml-auto">
              {formData.message.length}/5000 caracteres
            </p>
          </div>
        </div>

        {/* Opção de Enviar Email */}
        <div className="flex items-center space-x-3 p-4 bg-background-glass-hover rounded-button border border-border-glass">
          <input
            type="checkbox"
            id="sendEmail"
            checked={formData.sendEmail}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, sendEmail: e.target.checked }))
            }
            className="w-4 h-4 text-secondary border-border-glass rounded focus:ring-secondary focus:ring-2"
          />
          <label htmlFor="sendEmail" className="flex items-center space-x-2 text-sm text-text-primary cursor-pointer">
            <Mail className="w-4 h-4 text-blue-400" />
            <span>Enviar também por email</span>
          </label>
        </div>

        {/* Preview do destinatário selecionado */}
        {selectedUser && (
          <div className="p-4 bg-secondary/10 border border-secondary/30 rounded-button">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">
                  {selectedUser.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="font-medium text-text-primary">{selectedUser.name}</div>
                <div className="text-sm text-text-muted">{selectedUser.email}</div>
              </div>
            </div>
          </div>
        )}
      </form>

      {/* Footer */}
      <div className="p-4 bg-background-glass-hover border-t border-border-glass/30 rounded-b-button flex items-center justify-between">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-6 py-2 bg-gradient-to-r from-primary to-secondary hover:brightness-110 text-white rounded-button shadow-glass text-sm font-bold flex items-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Enviando...</span>
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              <span>
                {formData.sendEmail ? 'Enviar Email' : 'Enviar Mensagem'}
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SendMessageModal;

